import { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Loader2, Calendar, FileText } from 'lucide-react';
import { purchasingAPI, PurchaseOrderLine, ReasonCode } from '../../lib/api';
import { toast } from 'sonner';
import { useFormatters } from '../../contexts/PreferencesContext';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface ShortageManagementProps {
  line: PurchaseOrderLine;
  onUpdate?: () => void;
}

export default function ShortageManagement({ line, onUpdate }: ShortageManagementProps) {
  const [reasonCodes, setReasonCodes] = useState<ReasonCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingReasons, setLoadingReasons] = useState(true);

  // Record shortage form state
  const [shortageQty, setShortageQty] = useState('');
  const [selectedReasonId, setSelectedReasonId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');

  // Resolve shortage form state
  const [resolutionNotes, setResolutionNotes] = useState('');

  useEffect(() => {
    fetchReasonCodes();
  }, []);

  const fetchReasonCodes = async () => {
    try {
      setLoadingReasons(true);
      // CR-5886: only selectable (active, non-archived) codes for new transactions.
      const response = await purchasingAPI.getReasonCodes({ is_active: true });
      setReasonCodes(response.data.results || []);
    } catch (err: unknown) {
      console.error('Failed to fetch reason codes:', err);
      toast.error('Failed to load reason codes');
    } finally {
      setLoadingReasons(false);
    }
  };

  const handleRecordShortage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!shortageQty || parseFloat(shortageQty) <= 0) {
      toast.error('Please enter a valid shortage quantity');
      return;
    }

    if (!selectedReasonId) {
      toast.error('Please select a reason code');
      return;
    }

    try {
      setLoading(true);
      await purchasingAPI.recordLineShortage(line.id, {
        shortage_qty: shortageQty,
        reason_id: selectedReasonId,
        notes: notes,
      });

      toast.success('Shortage recorded successfully');

      // Reset form
      setShortageQty('');
      setSelectedReasonId(null);
      setNotes('');

      if (onUpdate) {
        onUpdate();
      }
    } catch (err: unknown) {
      console.error('Failed to record shortage:', err);
      toast.error(getApiErrorMessage(err, 'Failed to record shortage'));
    } finally {
      setLoading(false);
    }
  };

  const handleResolveShortage = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      await purchasingAPI.resolveLineShortage(line.id, {
        resolution_notes: resolutionNotes,
      });

      toast.success('Shortage resolved successfully');

      // Reset form
      setResolutionNotes('');

      if (onUpdate) {
        onUpdate();
      }
    } catch (err: unknown) {
      console.error('Failed to resolve shortage:', err);
      toast.error(getApiErrorMessage(err, 'Failed to resolve shortage'));
    } finally {
      setLoading(false);
    }
  };

  const { formatDateTime: contextFormatDateTime } = useFormatters();

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return contextFormatDateTime(dateString);
  };

  const hasActiveShortage = line.shortage_qty && !line.shortage_resolved;

  return (
    <div className="space-y-6">
      {/* Current Shortage Status */}
      {hasActiveShortage && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="bg-red-100 rounded-full p-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-4">Active Shortage</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium text-red-700 mb-1">Shortage Quantity</p>
                  <p className="text-xl font-bold text-red-900">{line.shortage_qty}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-red-700 mb-1">Recorded Date</p>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-red-600" />
                    <p className="text-sm font-medium text-red-900">{formatDate(line.shortage_recorded_date)}</p>
                  </div>
                </div>
              </div>
              {line.shortage_notes && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-red-700 mb-1">Notes</p>
                  <div className="bg-white border border-red-200 rounded p-3">
                    <p className="text-sm text-red-900 whitespace-pre-wrap">{line.shortage_notes}</p>
                  </div>
                </div>
              )}

              {/* Resolve Shortage Form */}
              <div className="bg-white border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-900 mb-3">Resolve Shortage</h4>
                <form onSubmit={handleResolveShortage} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      Resolution Notes (Optional)
                    </label>
                    <textarea
                      value={resolutionNotes}
                      onChange={(e) => setResolutionNotes(e.target.value)}
                      placeholder="Describe how the shortage was resolved..."
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                      rows={3}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Resolving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Mark as Resolved
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resolved Shortage Info */}
      {line.shortage_qty && line.shortage_resolved && (
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="bg-green-100 rounded-full p-3">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-green-900 mb-4">Shortage Resolved</h3>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium text-green-700 mb-1">Shortage Quantity</p>
                  <p className="text-lg font-bold text-green-900">{line.shortage_qty}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-green-700 mb-1">Recorded Date</p>
                  <p className="text-sm text-green-900">{formatDate(line.shortage_recorded_date)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-green-700 mb-1">Resolved Date</p>
                  <p className="text-sm text-green-900">{formatDate(line.shortage_resolved_date)}</p>
                </div>
              </div>
              {line.shortage_notes && (
                <div>
                  <p className="text-sm font-medium text-green-700 mb-1">Notes</p>
                  <div className="bg-white border border-green-200 rounded p-3">
                    <p className="text-sm text-green-900 whitespace-pre-wrap">{line.shortage_notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Record New Shortage Form */}
      {!hasActiveShortage && (
        <div className="border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="h-6 w-6 text-orange-600" />
            <h3 className="text-lg font-semibold text-primary">Record New Shortage</h3>
          </div>

          {loadingReasons ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <form onSubmit={handleRecordShortage} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Shortage Quantity <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.001"
                  value={shortageQty}
                  onChange={(e) => setShortageQty(e.target.value)}
                  placeholder="Enter shortage quantity"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedReasonId || ''}
                  onChange={(e) => setSelectedReasonId(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  required
                >
                  <option value="">Select a reason...</option>
                  {reasonCodes.map((reason) => (
                    <option key={reason.id} value={reason.id}>
                      {reason.code} - {reason.description}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any additional notes about the shortage..."
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                  rows={4}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Recording...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    Record Shortage
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      )}

      {/* No Shortage Message */}
      {!line.shortage_qty && (
        <div className="bg-muted/30 border rounded-lg p-6 text-center">
          <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
          <p className="text-lg font-semibold text-primary mb-1">No Shortages Recorded</p>
          <p className="text-sm text-muted-foreground">
            Use the form above to record a shortage if needed
          </p>
        </div>
      )}
    </div>
  );
}
